import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Bot, Check, X, FileText, AlertTriangle } from "lucide-react";

export default function Aprobaciones({ user, onLogout }) {
  const [tab, setTab] = useState("hechos");
  const [estado, setEstado] = useState("pendiente");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rechazoId, setRechazoId] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [procesando, setProcesando] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/aprobaciones/${tab}?estado=${estado}`);
      setItems(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error cargando propuestas"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab, estado]);

  const aprobar = async (id) => {
    setProcesando(true);
    try {
      const res = await api.post(`/admin/aprobaciones/hechos/${id}/aprobar`, {});
      toast.success(`Hecho N° ${res.data.numero_hecho} agregado al expediente`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error aprobando"); }
    finally { setProcesando(false); }
  };

  const rechazar = async () => {
    if (motivo.trim().length < 10) { toast.error("El motivo debe tener al menos 10 caracteres"); return; }
    setProcesando(true);
    try {
      await api.post(`/admin/aprobaciones/${tab}/${rechazoId}/rechazar`, { motivo });
      toast.success("Propuesta rechazada");
      setRechazoId(null); setMotivo("");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error rechazando"); }
    finally { setProcesando(false); }
  };

  if (user.actuacion !== "CEO") {
    return (<div><TopNav user={user} onLogout={onLogout} /><div className="container mx-auto p-8 text-sm text-gray-500">Solo el administrador puede ver esta página.</div></div>);
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <TopNav user={user} onLogout={onLogout} />
      <div className="container mx-auto px-6 py-6" data-testid="aprobaciones-page">
        <h1 className="text-2xl font-bold mb-1" style={{ color: "var(--text)" }}>Bandeja de Aprobación</h1>
        <p className="text-xs mb-5" style={{ color: "var(--text-muted)" }}>Propuestas hechas por IA vía MCP. Nada entra al expediente sin tu aprobación.</p>

        <div className="flex gap-2 mb-4">
          {["hechos", "casos"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`aprobaciones-tab-${t}`}
              className="px-4 py-2 rounded-lg text-xs font-medium capitalize"
              style={{ background: tab === t ? "var(--primary)" : "var(--surface)", color: tab === t ? "white" : "var(--text-secondary)" }}>{t}</button>
          ))}
          <select value={estado} onChange={e => setEstado(e.target.value)} className="ml-auto text-xs border rounded-lg px-3" data-testid="aprobaciones-filtro-estado">
            <option value="pendiente">Pendientes</option>
            <option value="aprobado">Aprobadas</option>
            <option value="rechazado">Rechazadas</option>
            <option value="retirado">Retiradas</option>
            <option value="todos">Todas</option>
          </select>
        </div>

        {loading ? <p className="text-xs text-gray-400">Cargando…</p> :
          items.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="aprobaciones-vacio">
              No hay propuestas {estado !== "todos" ? `en estado "${estado}"` : ""}. Cuando Claude proponga {tab} vía MCP aparecerán aquí.
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              {items.map(p => (
                <Card key={p.id} data-testid={`propuesta-card-${p.id}`}>
                  <CardContent className="pt-5">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className="bg-violet-100 text-violet-800 border border-violet-300"><Bot className="h-3 w-3 mr-1" />IA · {p.autor_nombre}</Badge>
                      {p.caso && <Badge variant="outline">Caso #{p.caso.numero_caso} — {p.caso.titulo_caso}</Badge>}
                      <Badge variant="secondary">{String(p.fecha_evento || p.fecha_propuesta).slice(0, 10)}</Badge>
                      {p.confianza != null && <Badge variant="outline">Confianza: {Math.round(p.confianza * 100)}%</Badge>}
                      <Badge className={p.estado === "pendiente" ? "bg-amber-100 text-amber-800" : p.estado === "aprobado" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}>{p.estado}</Badge>
                    </div>
                    <h3 className="font-semibold text-sm mb-1" style={{ color: "var(--text)" }}>{p.titulo_evento || p.titulo_caso}</h3>
                    <p className="text-xs whitespace-pre-wrap mb-2" style={{ color: "var(--text-secondary)" }}>{p.relato_usuario || p.descripcion}</p>
                    {p.personas_concurrentes && <p className="text-[11px] mb-1" style={{ color: "var(--text-muted)" }}>Personas: {p.personas_concurrentes}</p>}
                    {p.justificacion && <p className="text-[11px] italic mb-2" style={{ color: "var(--text-muted)" }}>Justificación IA: {p.justificacion}</p>}
                    {(p.evidencias_referenciadas || []).length > 0 && (
                      <div className="mb-2">
                        <p className="text-[11px] font-semibold" style={{ color: "var(--text-secondary)" }}><FileText className="h-3 w-3 inline mr-1" />Evidencias citadas:</p>
                        {p.evidencias_referenciadas.map((ev, i) => (
                          <p key={i} className="text-[11px] ml-4" style={{ color: "var(--text-muted)" }}>· {ev.tipo}{ev.asunto ? ` — ${ev.asunto}` : ""}{ev.cita_textual ? ` — "${ev.cita_textual.slice(0, 120)}…"` : ""}</p>
                        ))}
                      </div>
                    )}
                    {p.hecho_duplicado && (
                      <div className="text-[11px] p-2 rounded bg-amber-50 border border-amber-300 text-amber-800 mb-2" data-testid={`duplicado-aviso-${p.id}`}>
                        <AlertTriangle className="h-3 w-3 inline mr-1" />Posible duplicado del Hecho N° {p.hecho_duplicado.numero_hecho}: {p.hecho_duplicado.titulo_evento}
                      </div>
                    )}
                    {p.estado === "rechazado" && p.motivo_rechazo && <p className="text-[11px] text-red-600 mb-2">Motivo de rechazo: {p.motivo_rechazo} ({p.revisado_por})</p>}
                    {p.estado === "aprobado" && <p className="text-[11px] text-green-700 mb-2">Aprobado por {p.revisado_por} el {String(p.fecha_revision).slice(0, 10)}</p>}
                    {p.estado === "pendiente" && tab === "hechos" && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={() => aprobar(p.id)} disabled={procesando} className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid={`aprobar-btn-${p.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" />Aprobar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setRechazoId(p.id); setMotivo(""); }} disabled={procesando} className="h-8 text-xs text-red-600 border-red-300" data-testid={`rechazar-btn-${p.id}`}>
                          <X className="h-3.5 w-3.5 mr-1" />Rechazar
                        </Button>
                      </div>
                    )}
                    {p.estado === "pendiente" && tab === "casos" && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => { setRechazoId(p.id); setMotivo(""); }} disabled={procesando} className="h-8 text-xs text-red-600 border-red-300" data-testid={`rechazar-caso-btn-${p.id}`}>
                          <X className="h-3.5 w-3.5 mr-1" />Rechazar
                        </Button>
                        <p className="text-[11px] self-center" style={{ color: "var(--text-muted)" }}>Para aprobar un caso propuesto, créalo manualmente desde "Nuevo" usando estos datos.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

        <Dialog open={!!rechazoId} onOpenChange={() => setRechazoId(null)}>
          <DialogContent data-testid="rechazo-modal">
            <DialogHeader><DialogTitle>Rechazar propuesta</DialogTitle></DialogHeader>
            <Textarea value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo del rechazo (mínimo 10 caracteres). La IA lo verá para aprender." data-testid="rechazo-motivo-input" />
            <Button onClick={rechazar} disabled={procesando} className="bg-red-600 hover:bg-red-700 text-white" data-testid="rechazo-confirmar-btn">Confirmar rechazo</Button>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
